import { zodResolver } from '@hookform/resolvers/zod';
import { add, addSeconds, format, formatISO, isValid, set } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { type UseFormSetValue, type UseFormWatch, useForm } from 'react-hook-form';
import { useCustomTranslation } from '../../../../../../hooks';
import { useAddTaskToPlanMutation } from '../../../../../../queryHooks/useAddTaskToPlanMutation';
import { useAvailableQueues } from '../../../../../../queryHooks/useAvailableQueues';
import { useTaskDurationForecast } from '../../../../../../queryHooks/useTaskDurationForecast';
import type { FormDurationInputSchema } from '../../../../../../components/molecules/FormComponents';
import { type AddToPlanFormSchema, getAddToPlanFormSchema } from './getAddToPlanFormSchema';

const secondsToDurationInput = (totalSeconds: number): FormDurationInputSchema => {
  const totalMinutes = Math.floor(totalSeconds / 60);

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
};

const getSeconds = (duration: FormDurationInputSchema) => {
  const { days = 0, hours = 0, minutes = 0 } = duration;
  const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
  return totalMinutes * 60;
};

type UseTaskFormProps = {
  taskId: number;
  taskName: string;
  onSuccess: () => void;
  queueId?: number;
  start?: string;
  end?: string;
  isLocked?: boolean;
};

export const useAddTaskForm = ({ taskId, taskName, queueId, onSuccess, isLocked }: UseTaskFormProps) => {
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.addToPlanForm');
  const addTaskToPlan = useAddTaskToPlanMutation();

  const form = useForm<AddToPlanFormSchema>({
    mode: 'onSubmit',
    shouldUnregister: false,
    resolver: zodResolver(getAddToPlanFormSchema(t)),
    defaultValues: {
      blockedStart: isLocked,
      hasCustomProductionDuration: false,
      hasCustomRetoolingDuration: false,
    },
  });

  const queue = form.watch('queue');
  useUpdateSuggestedDates({
    taskId: taskId.toString(),
    setValue: form.setValue,
    selectedQueueId: queue?.queueId || queueId,
  });
  useUpdateEndDate({ taskId: taskId.toString(), watch: form.watch, setValue: form.setValue });

  const onSubmit = useCallback(
    async (formData: AddToPlanFormSchema) => {
      const [startHours, startMinutes] = formData.start.time.split(':');
      const requestStartDate = set(new Date(formData.start.date), {
        hours: Number(startHours),
        minutes: Number(startMinutes),
      });
      const overrideDuration =
        formData.hasCustomProductionDuration && formData.overrideProductionDuration
          ? getSeconds(formData.overrideProductionDuration)
          : undefined;

      if (!isValid(requestStartDate)) {
        form.setError('start.date', {
          type: 'custom',
          message: tWrapper('errors.invalidStartDate'),
        });
      }
      if (isValid(requestStartDate)) {
        const data = {
          taskAddEntry: {
            taskId: taskId,
            queueId: formData.queue.queueId,
            startTime: requestStartDate.toISOString(),
            blockedStart: formData.blockedStart,
            overrideDuration,
          },
        };
        addTaskToPlan.mutate({ data, taskName }, { onSuccess });
      }
    },
    [form, addTaskToPlan, taskName, tWrapper, taskId, onSuccess],
  );

  return {
    form,
    onSubmit,
    isPending: addTaskToPlan.isPending,
  };
};

type UseUpdateSuggestedDatesProps = {
  taskId: string;
  selectedQueueId?: number;
  setValue: UseFormSetValue<AddToPlanFormSchema>;
};

const useUpdateSuggestedDates = ({ taskId, selectedQueueId, setValue }: UseUpdateSuggestedDatesProps) => {
  const { data } = useAvailableQueues(taskId);

  useEffect(() => {
    if (!data) {
      return;
    }

    const { suggestedQueueId, queues } = data;

    const selectedQueue = queues?.find(({ queueId }) => queueId === selectedQueueId);
    const suggestedQueue = queues?.find(({ queueId }) => queueId === suggestedQueueId);
    const queue = selectedQueue || suggestedQueue;

    if (queue) {
      const startDate = new Date(queue.suggestedStart);
      const endDate = new Date(queue.suggestedEnd);

      if (!isValid(startDate) || !isValid(endDate)) {
        return;
      }

      const start = { date: startDate, time: format(startDate, 'HH:mm') };
      const end = { date: endDate, time: format(endDate, 'HH:mm') };

      setValue('start', start);
      setValue('end', end);
      setValue('queue', queue);
      setValue('overrideProductionDuration', secondsToDurationInput(queue.duration));
    }
  }, [data, selectedQueueId, setValue]);
};

type UseUpdateEndDateProps = {
  taskId: string;
  watch: UseFormWatch<AddToPlanFormSchema>;
  setValue: UseFormSetValue<AddToPlanFormSchema>;
};

const useUpdateEndDate = ({ taskId, watch, setValue }: UseUpdateEndDateProps) => {
  const [queue, start, hasCustomProductionDuration, overrideProductionDuration] = watch([
    'queue',
    'start',
    'hasCustomProductionDuration',
    'overrideProductionDuration',
  ]);
  const startDate = parseStartDate(start);
  const queueId = queue?.queueId.toString() || '';

  const { data: durationInSeconds } = useTaskDurationForecast(
    { taskId, queueId, start: startDate },
    !!startDate && !!queueId && !hasCustomProductionDuration,
  );

  useEffect(() => {
    if (!durationInSeconds) {
      return;
    }

    const parsedStartDate = new Date(startDate);

    if (!isValid(parsedStartDate)) {
      return;
    }

    const endDate = addSeconds(parsedStartDate, durationInSeconds);
    const end = { date: endDate, time: format(endDate, 'HH:mm') };
    setValue('end', end);
  }, [durationInSeconds, startDate, setValue, hasCustomProductionDuration]);

  useEffect(() => {
    const parsedStartDate = new Date(startDate);
    if (hasCustomProductionDuration && overrideProductionDuration && isValid(parsedStartDate)) {
      const endDate = add(parsedStartDate, overrideProductionDuration);
      const end = { date: endDate, time: format(endDate, 'HH:mm') };

      setValue('end', end);
    }
  }, [hasCustomProductionDuration, overrideProductionDuration, startDate, setValue]);
};

const parseStartDate = (start?: AddToPlanFormSchema['start']) => {
  const startDate = start?.date;
  const [startHours, startMinutes] = start?.time?.split(':') || [];

  if (!startDate || !startHours || !startMinutes) {
    return '';
  }

  const correctDate = set(new Date(start.date), { hours: Number(startHours), minutes: Number(startMinutes) });

  if (!isValid(correctDate)) {
    return '';
  }

  return formatISO(correctDate);
};
